/**
 * Dynamic mock processing engine.
 *
 * Generates realistic Traffic Light mapping results using the
 * ACTUAL source columns from the user's uploaded file matched
 * against the Lloyd's 5.2 schema.
 *
 * Behaviour:
 *  - Uses a simple token-overlap similarity score to rank matches
 *  - Assigns Green / Amber / Red tiers based on score thresholds
 *  - Deliberately introduces realistic demo errors:
 *      * 2-4 mandatory targets left unmatched (target_to_source, needs_match)
 *      * 1 duplicate target (two source cols mapped to the same target)
 *      * Several low-confidence amber suggestions
 *  - Unmapped source columns appear in the additional tab with no target
 *
 * All randomness uses a seeded PRNG so results are reproducible per
 * set of source columns (same file -> same mock mappings).
 */

import type { MappingRow, MappingStatus, SchemaField } from "@/types";
import { LLOYDS_52_SCHEMA } from "./mappingData";

// ── Seeded PRNG (mulberry32) ──────────────────────────────────

function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

// ── Token-overlap similarity ──────────────────────────────────

/** Normalise a string for comparison: lowercase, strip punctuation, split on boundaries */
function tokenise(s: string): Set<string> {
  const cleaned = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  // Also split on camelCase / snake_case boundaries
  const expanded = cleaned
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 0);
  return new Set(expanded);
}

/** Score 0-1 based on token overlap between source column name and schema field name + definition */
function similarity(source: string, field: SchemaField): number {
  const srcTokens = tokenise(source);
  const fieldTokens = new Set([...tokenise(field.fieldName), ...tokenise(field.definition)]);

  if (srcTokens.size === 0 || fieldTokens.size === 0) return 0;

  let matches = 0;
  for (const t of srcTokens) {
    for (const ft of fieldTokens) {
      // Exact token match or one is a prefix of the other (3+ chars)
      if (t === ft || (t.length >= 3 && ft.startsWith(t)) || (ft.length >= 3 && t.startsWith(ft))) {
        matches++;
        break;
      }
    }
  }

  return matches / srcTokens.size;
}

// ── Tier assignment ───────────────────────────────────────────

interface ScoredMatch {
  sourceColumn: string;
  field: SchemaField;
  score: number;
}

function assignTier(
  score: number,
  rng: () => number
): { status: MappingStatus; confidence: number } {
  // High score + some luck = Green (known mapping)
  if (score >= 0.7 && rng() < 0.6) {
    return { status: "known_mapping", confidence: 0.95 + rng() * 0.05 };
  }
  // Medium score = Amber (suggested)
  if (score >= 0.3) {
    return { status: "ML", confidence: 0.55 + score * 0.35 + (rng() * 0.1 - 0.05) };
  }
  // Low score but still a match = weak suggestion
  if (score > 0) {
    return { status: "ML", confidence: 0.3 + rng() * 0.2 };
  }
  return { status: "needs_match", confidence: 0 };
}

// ── Main generator ────────────────────────────────────────────

let _idCounter = 0;
function makeId(): string {
  return `mock-${++_idCounter}`;
}

export interface MockProcessResult {
  rows: MappingRow[];
  sourceColumns: string[];
  duplicateColumns: string[];
  processingTimeMs: number;
}

/**
 * Generate realistic mock mapping results from the user's actual source columns.
 *
 * @param sourceColumns - Column headers extracted from the uploaded file
 * @param schemaFields  - Target schema fields (defaults to LLOYDS_52_SCHEMA)
 */
export function generateDynamicMappings(
  sourceColumns: string[],
  schemaFields: SchemaField[] = LLOYDS_52_SCHEMA
): MockProcessResult {
  _idCounter = 0;
  const seed = hashString(sourceColumns.join("|"));
  const rng = mulberry32(seed);

  const mandatoryFields = schemaFields.filter((f) => f.mandatory);
  const additionalFields = schemaFields.filter((f) => !f.mandatory);

  // ── Step 1: Score all source-target pairs ────────────────

  const allScores: ScoredMatch[] = [];
  for (const src of sourceColumns) {
    for (const field of schemaFields) {
      const score = similarity(src, field);
      if (score > 0) {
        allScores.push({ sourceColumn: src, field, score });
      }
    }
  }

  // Sort by score descending
  allScores.sort((a, b) => b.score - a.score);

  // ── Step 2: Greedy 1:1 assignment (best match first) ─────

  const assignedSources = new Set<string>();
  const assignedTargets = new Set<string>();
  const assignments: ScoredMatch[] = [];

  // Decide how many mandatory fields to deliberately leave unmatched (2-4)
  const numMandatoryToSkip = 2 + Math.floor(rng() * 3); // 2, 3, or 4
  const mandatoryIdsToSkip = new Set<string>();

  // Pick random mandatory fields to skip
  const shuffledMandatory = [...mandatoryFields].sort(() => rng() - 0.5);
  for (let i = 0; i < Math.min(numMandatoryToSkip, shuffledMandatory.length); i++) {
    mandatoryIdsToSkip.add(shuffledMandatory[i]!.id);
  }

  for (const match of allScores) {
    if (assignedSources.has(match.sourceColumn)) continue;
    if (assignedTargets.has(match.field.id)) continue;
    // Skip deliberately unmatched mandatory targets
    if (mandatoryIdsToSkip.has(match.field.id)) continue;

    assignedSources.add(match.sourceColumn);
    assignedTargets.add(match.field.id);
    assignments.push(match);
  }

  // ── Step 3: Introduce one duplicate target error ─────────
  // Pick a matched mandatory target and assign a second source column to it

  let duplicateRow: MappingRow | null = null;
  const mandatoryAssignments = assignments.filter((a) => a.field.mandatory);
  if (mandatoryAssignments.length > 0) {
    const dupeTarget = mandatoryAssignments[Math.floor(rng() * mandatoryAssignments.length)]!;
    const unmatchedSources = sourceColumns.filter((s) => !assignedSources.has(s));
    if (unmatchedSources.length > 0) {
      const dupeSrc = unmatchedSources[Math.floor(rng() * unmatchedSources.length)]!;
      assignedSources.add(dupeSrc);
      duplicateRow = {
        id: makeId(),
        sourceColumn: dupeSrc,
        targetFieldId: dupeTarget.field.id,
        direction: "source_to_target",
        status: "ML",
        confidence: 0.55 + rng() * 0.2,
        approved: false,
        suggestions: [dupeTarget.field.id],
      };
    }
  }

  // ── Step 4: Build rows ───────────────────────────────────

  const rows: MappingRow[] = [];

  // Matched rows (source_to_target)
  for (const match of assignments) {
    const { status, confidence } = assignTier(match.score, rng);

    // Build suggestions: the matched target plus 1-2 runners-up
    const suggestions = [match.field.id];
    const runners = allScores
      .filter(
        (s) =>
          s.sourceColumn === match.sourceColumn && s.field.id !== match.field.id && s.score > 0.1
      )
      .slice(0, 2);
    for (const r of runners) {
      suggestions.push(r.field.id);
    }

    rows.push({
      id: makeId(),
      sourceColumn: match.sourceColumn,
      targetFieldId: match.field.id,
      direction: "source_to_target",
      status,
      confidence: Math.round(confidence * 100) / 100,
      approved: status === "known_mapping",
      suggestions,
    });
  }

  // Add the duplicate row after its original
  if (duplicateRow) {
    rows.push(duplicateRow);
  }

  // Unmatched mandatory targets (target_to_source)
  for (const field of mandatoryFields) {
    if (assignedTargets.has(field.id)) continue;

    // Suggest top source columns that haven't been assigned
    const candidates = sourceColumns
      .filter((s) => !assignedSources.has(s))
      .map((s) => ({ source: s, score: similarity(s, field) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((c) => c.source);

    // If we don't have enough unassigned candidates, include some assigned ones
    if (candidates.length < 3) {
      const additionalCandidates = sourceColumns
        .filter((s) => !candidates.includes(s))
        .map((s) => ({ source: s, score: similarity(s, field) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 3 - candidates.length)
        .map((c) => c.source);
      candidates.push(...additionalCandidates);
    }

    rows.push({
      id: makeId(),
      sourceColumn: "",
      targetFieldId: field.id,
      direction: "target_to_source",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: candidates,
    });
  }

  // Unmatched source columns -> additional tab rows with no target
  const unmatchedSources = sourceColumns.filter((s) => !assignedSources.has(s));
  for (const src of unmatchedSources) {
    // Find best additional-field suggestions
    const suggestions = additionalFields
      .filter((f) => !assignedTargets.has(f.id))
      .map((f) => ({ field: f, score: similarity(src, f) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .filter((s) => s.score > 0)
      .map((s) => s.field.id);

    rows.push({
      id: makeId(),
      sourceColumn: src,
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions,
    });
  }

  // Simulate realistic processing time (400-1800ms)
  const processingTimeMs = Math.round(400 + rng() * 1400);

  return {
    rows,
    sourceColumns,
    duplicateColumns: [],
    processingTimeMs,
  };
}
